import React, { useEffect, useRef, useState } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { PartialMember, getPartialMember, useSendKeycloakVerificationLink } from '../../hooks/api/useUser';
import { ROUTE_PATH } from '../../routes/route-paths';
import Toast from '../../components/common/pop-up-message';
import { LoginButton } from '../../components/login-button';
import { OnboardingLayout } from '../onboarding/common/layout';
import { ArrowButton } from '../../assets/images/svgs';

export const VerificationPage: React.FunctionComponent = (): JSX.Element => {
  const navigate = useHistory();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const emailAddress = queryParams.get('email_address');
  const mode = queryParams.get('mode');
  const sendKeycloakVerificationLink = useSendKeycloakVerificationLink();
  const hasRunOnce = useRef(false);

  const [partialMember, setPartialMember] = useState<PartialMember | undefined>(undefined);
  const [linkSent, setLinkSent] = useState(false);

  const isWelcome = mode === 'welcome';

  const sendVerificationLink = React.useCallback(
    (type: 'text' | 'email', partialMember?: PartialMember, isResend = false) => {
      if (!partialMember) {
        return;
      }
      sendKeycloakVerificationLink.mutate(
        {
          userId: partialMember.user_id,
        },
        {
          onSuccess: () => {
            if (isResend) {
              setLinkSent(true);
              Toast.success('Verification link sent successfully.');
            }
          },
          onError: (err: any) => {
            Toast.error(err.response.data.description);
          },
        },
      );
    },
    [sendKeycloakVerificationLink],
  );

  useEffect(() => {
    if (hasRunOnce.current) return;
    hasRunOnce.current = true;

    getPartialMember(
      { user_id: JSON.parse(sessionStorage.getItem('partial_member_id')!) },
      (partialMember) => {
        setPartialMember(partialMember);
        sendVerificationLink('email', partialMember);
      },
      (err) => {
        console.error(err);
        Toast.error("That didn't work. please try again.");
        navigate.push(ROUTE_PATH.SIGNUP);
      },
    );
  }, [navigate, sendVerificationLink]);

  // we don't want a situation where the user can access this page without an email address, like navigating straight to it
  if (!emailAddress) {
    return <Redirect to={ROUTE_PATH.SIGNUP} />;
  }

  // Show link-sent state if link was just sent
  if (linkSent) {
    return (
      <OnboardingLayout>
        <div className="mb-6 space-y-1">
          <h1 className="font-albert-sans text-xl font-semibold leading-[1.5] text-black">Check your email</h1>
          <p className="font-open-sans text-sm font-normal leading-[1.5] text-black">
            We've sent you a new invite link.
          </p>
        </div>

        <div className="mb-6 flex flex-col items-center gap-3">
          <p className="font-open-sans w-[432px] text-center text-sm font-normal leading-[1.5] text-black">
            Didn't get an email? <br className="md:hidden" /> Click the button below to resend.
          </p>
          <button
            data-testid="resend-email-button"
            onClick={() => {
              sendVerificationLink('email', partialMember, true);
            }}
            className="text-primary font-albert-sans bg-button-secondary hover:bg-button-secondary-hover rounded-full px-7 py-[10px] text-base font-medium leading-5 transition-colors"
          >
            Resend link to my email
          </button>
          <p className="font-open-sans w-[432px] text-center text-sm font-normal leading-[1.5] text-black">or</p>
          <button
            onClick={() => {
              navigate.push(ROUTE_PATH.LOGIN);
            }}
            className="font-albert-sans bg-button-primary hover:bg-button-primary-hover w-[232px] rounded-full px-7 py-[10px] text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors"
          >
            Log in
          </button>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Link className="text-primary flex items-center gap-1.5 hover:cursor-pointer" to={ROUTE_PATH.SIGNUP}>
            <ArrowButton backgroundColor="rgb(var(--color-button-secondary))" direction="left" className="h-5 w-5" />
            <span className="font-albert-sans text-xs font-medium leading-6">Sign up</span>
          </Link>
          <div className="flex items-center gap-4">
            <LoginButton />
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout>
      <div className="mb-6 space-y-1">
        <h1 className="font-albert-sans text-xl font-semibold leading-[1.5] text-black">
          {isWelcome ? 'We just sent you a link' : "Hmm... that didn't work."}
        </h1>
        <p className="font-open-sans text-sm font-normal leading-[1.5] text-black">
          {isWelcome
            ? 'We just sent you an email. Please click the link in the email to verify your account. '
            : 'It looks like the verification code is incorrect or may be expired. We can resend you a new link by email or text'}
        </p>
      </div>

      <div className="mb-6 flex flex-col items-center gap-6">
        {isWelcome && (
          <p className="font-open-sans text-center text-sm font-normal leading-[1.5] text-black">
            Didn't get an email? <br className="md:hidden" />
            Click the button below to resend
          </p>
        )}
        <button
          data-testid="resend-email-button"
          onClick={() => {
            sendVerificationLink('email', partialMember, true);
          }}
          className="font-albert-sans text-primary w-full text-base font-medium leading-5"
        >
          Resend Verification Link
        </button>
      </div>

      {partialMember?.user_email_obfuscated && (
        <div className="mb-6 flex w-full justify-center py-4">
          <p className="font-open-sans text-xs font-normal leading-6 text-black">
            We sent the email to {partialMember.user_email_obfuscated}
          </p>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between text-nowrap">
        <Link
          className="text-primary flex items-center justify-center gap-1.5 hover:cursor-pointer"
          to={ROUTE_PATH.SIGNUP}
        >
          <ArrowButton backgroundColor="rgb(var(--color-button-secondary))" direction="left" className="h-5 w-5" />
          <span className="font-albert-sans text-xs font-medium leading-6">Back</span>
        </Link>
        <div className="flex items-center justify-center gap-4">
          <LoginButton />
        </div>
      </div>
    </OnboardingLayout>
  );
};
